import {
  HestiaFhirResourcesService,
  fhirResourceActions,
  selectHestiaCarePlanById,
} from '@hestia/ngx-fhir';
import { IDawbaCarePlan, IDawbaCarePlanActivity } from '../models/models';
import { Injectable, NgZone } from '@angular/core';
import {
  Observable,
  filter,
  first,
  firstValueFrom,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs';
import {
  QuestionnaireResponseService,
  initSession,
  qnrFormSessionActions,
  selectSessionByCarePlanAndActivity,
} from '@hestia/ngx-fhir-qnr-forms';
import {
  selectDawbaCarePlan,
  selectDawbaSpecificState,
  selectIsCarePlanLoaded,
} from '../store/+dawba-specific/dawba-specific.selectors';

import { DawbaRoutes } from '../route-constants';
import { HestiaCommonService } from '@hestia/ngx-common';
import { HestiaUserFacade } from '@hestia/ngx-user';
import { IDawbaSpecificState } from '../store/+dawba-specific/dawba-specific.reducer';
import { NavController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { dawbaSpecificActions } from '../store/+dawba-specific/dawba-specific.actions';
import { isQnrAdaptive } from '@hestia/ngx-fhir-qnr-forms';
import { v4 } from 'uuid';

@Injectable({ providedIn: 'root' })
export class DawbaAppService {
  topicConfig: any[];
  coreState: IDawbaSpecificState;
  carePlan$: Observable<IDawbaCarePlan> = this.store
    .select(selectDawbaSpecificState)
    .pipe(
      switchMap((dawbaState) =>
        this.store.select(selectDawbaCarePlan(dawbaState.curCarePlanId))
      )
    );
  /* Observable that checks if careplan has been loaded, doesn't return until it has */
  isCarePlanLoaded$ = this.store.select(selectIsCarePlanLoaded).pipe(
    filter((isLoaded) => isLoaded === true),
    first()
  );
  constructor(
    private userFacade: HestiaUserFacade,
    private store: Store,
    private fhirService: HestiaFhirResourcesService,
    private qnrResponseService: QuestionnaireResponseService,
    public common: HestiaCommonService,
    private navCtrl: NavController,
    private zone: NgZone
  ) {
    this.store
      .select(selectDawbaSpecificState)
      // eslint-disable-next-line ngrx/no-store-subscription
      .subscribe((state) => (this.coreState = state));
      this.loadTopicsConfig();
  }

  async initQnrFormSession(activity: IDawbaCarePlanActivity): Promise<string> {
    const parentCarePlan = await firstValueFrom(
      this.store.select(selectHestiaCarePlanById(activity.carePlanId))
    );
    // First check if questionnaire albready initialized, if not, init it
    return firstValueFrom(
      this.store
        .select(
          selectSessionByCarePlanAndActivity(activity.carePlanId, activity.code)
        )
        .pipe(
          withLatestFrom(this.userFacade.userState$),
          // eslint-disable-next-line ngrx/avoid-mapping-selectors
          map(([session, userState]) => {
            if (!session) {
              const values =
                this.qnrResponseService.getFormValuesFromQuestionnaireResponse(
                  activity.qnrResponseResource
                );
              const sessionId = activity?.qnrResponseResource?.id ?? v4();
              this.store.dispatch(
                initSession({
                  session: {
                    id: sessionId,
                    isAdaptive: isQnrAdaptive(activity.qnrResource),
                    questionnaireResponse: activity.qnrResponseResource,
                    doesQnrResponseExistOnServer: activity.qnrResponseResource
                      ? true
                      : false,
                    values: values,
                    displayTitle: activity.qnrResource.title,
                    questionnaireId: activity.qnrResource.id,
                    patientId: userState.fhirId,
                    carePlanId: activity.carePlanId,
                    carePlanStatus: parentCarePlan.carePlanResource.status,
                    activityStatus: activity.status,
                    activityCode: activity.code,
                    renderType: 'page',
                    subjectRef: { reference: userState.fhirId },
                    authorRef: { reference: userState.fhirId },
                    sourceRef: { reference: userState.fhirId },
                    allowMenu: true,
                    curLinkIdPath: null,
                  },
                })
              );
              return sessionId;
            } else {
              return session.id;
            }
          })
        )
    );
  }

  handleLogout(props: { navigateToRoot: boolean } = { navigateToRoot: false }) {
    this.store.dispatch(qnrFormSessionActions.clearSessions());
    this.store.dispatch(fhirResourceActions.clearFhirResources());
    this.store.dispatch(dawbaSpecificActions.resetState());
    this.fhirService.inflightFetchIds = new Set();
    if (props.navigateToRoot) {
      this.zone.run(() =>
        this.navCtrl.navigateRoot('/' + DawbaRoutes.LoginPage)
      );
    }
  }

  getDawbaActivityValues(activity) {
    if (!activity) {
      return null;
    }
    const values =
      this.qnrResponseService.getFormValuesFromQuestionnaireResponse(
        activity.qnrResponseResource
      );
    return values;
  }

  loadTopicsConfig(){
  fetch('./assets/topicsconfig/language_topics.json').then(res => res.json())
    .then(json => {
      this.topicConfig = json;
    });
  }
}
